import React, { useContext, useEffect, useRef, useState } from 'react'
import styles from './header.module.scss'
import Button from '../button/Button'
import { Link, useHistory, useLocation } from 'react-router-dom'
import logo from '../../assets/images/t_logo.png'
import cookie from 'cookie_js'
import { IState } from 'src/Interface'
import axios from 'axios'
import { baseApiURL } from 'src/utils/utils'
import { StateContext, DispatchContext } from '../../AppContext'
import info from '../../assets/images/info-large.png'
import { toast } from 'react-toastify'
import { gaEvenActions, gaEvent, gaEventCategories, gaLogout } from '../../utils/ga/ga'
import { gaOrderedTripMapper, gaOrderTypes, gaUserMapper } from 'src/utils/ga/gaMapper'
import { useQueryClient } from '@tanstack/react-query'
import { initialState } from 'src/reducers'

const Header = React.memo(function Header() {
  const dispatch: any = useContext(DispatchContext)
  const location = useLocation()
  const [showDropdown, setShowDropdown] = useState(false)
  const container = useRef(null)
  const state: IState = useContext(StateContext)
  const history = useHistory()
  const queryClient = useQueryClient()

  const logout = () => {
    // Clear all cookies - use set with past expiration to delete cookies with path
    const expiredDate = new Date(0)
    cookie.set('access_token', '', { expires: expiredDate, path: '/' })
    cookie.set('refresh_token', '', { expires: expiredDate, path: '/' })
    cookie.set('user_id', '', { expires: expiredDate, path: '/' })

    // Clear React Query cache to prevent stale data from previous session
    queryClient.clear()

    // Reset all reducer state to initial values
    dispatch({ type: 'SET_USER_INFO', user: initialState.user })
    dispatch({ type: 'SET_COMPANY', company: initialState.company })
    dispatch({ type: 'SET_BRANCH', branch: initialState.branch })
    dispatch({ type: 'SET_ORDERS', orders: [] })
    dispatch({ type: 'SET_USER_ORDERS', userOrders: [] })
    dispatch({ type: 'SET_VOUCHERS', vouchers: [], filters: initialState.voucherFilters })
    dispatch({ type: 'SET_COMPANIES', companies: [] })
    dispatch({ type: 'SET_BRANCHES', branches: [] })
    dispatch({ type: 'SET_ACCOUNTS', accounts: [] })
    dispatch({ type: 'SET_USERS', users: [] })

    // Log analytics event
    gaLogout()

    // Navigate to login page
    history.push('/login')
  }

  const clickedOutSide = (e: any) => {
    if (container.current && !container.current.contains(e.target)) {
      setShowDropdown(false)

    }
  }

  useEffect(() => {
    window.addEventListener('click', clickedOutSide)
    return () => window.removeEventListener('click', clickedOutSide)
  }, [])

  const quickOrder = () => {
    axios.post(`${baseApiURL()}/orders/order_now`, {},
      {
        headers: {
          Authorization: `Bearer ${state.user.accessToken}`
        }
      }).then(result => {
        history.push('/order')
        toast('Ordre oprettet')

        dispatch({
          type: 'SET_USER_ORDERS',
          userOrders: [
            result.data,
            ...state.userOrders
          ]
        })
        gaEvent(gaEventCategories.orders, gaEvenActions.ordered_trip, gaUserMapper(state), gaOrderedTripMapper(gaOrderTypes.quick_order, null, result.data))
      }).catch((e) => {
        toast(e.response.data.message)
      })
  }

  return (
    <header className={styles.header} id='header'>
      <div className={styles.logo}>
        <span className={styles.img}>
          <Link to='/order'><img src={logo} alt="" /></Link>
        </span>
        <hr className={styles.divider} />
        <span className={styles.headerLabel}>
          <label htmlFor="">tlf. 4583 8385</label>
        </span>
      </div>
      <nav className='tw-mx-auto'>
        <ul className={styles.list}>
          <li className={location.pathname === '/orders' ? styles.active : ''}><Link to='/orders'>Overblik</Link></li>
          <li className={location.pathname === '/order' ? styles.active : ''}><Link to='/order'>Bestilling</Link></li>
          {window.showV2Features && state.user.role !== 'BranchEmployee' && <li className={location.pathname === '/vouchers' ? styles.active : ''}><Link to='/vouchers'>Taxibon</Link></li>}
        </ul>
      </nav>
      <div
        className={styles.avatarContainer}
        ref={container}>

        {state.user.firstName && state.user.lastName && <div onClick={() => setShowDropdown(!showDropdown)} className={styles.avatar}>
          <span className={styles.userInitials}>{state.user.firstName.charAt(0).toUpperCase()}{state.user.lastName.charAt(0).toUpperCase()}</span>
        </div>}
        {showDropdown && <ul className={`${styles.dropDownMenu}`}>
          {state.user.role === 'DanTaxiAdmin' &&
            <Link to='/companies'>
              <li>
                <span className={styles.icons}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                    <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
                  </svg>
                </span>
                <span>Kunder</span>
              </li>
            </Link>
          }
          {state.user.role !== 'BranchEmployee' &&
            <Link to='/settings'>
              <li>
                <span className={styles.icons}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                  </svg>
                </span>
                Indstillinger
              </li>
            </Link>}
          <button onClick={logout} type='button' className='tw-flex'>
            <li >
              <span className={styles.icons}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
                </svg>
              </span>
              Log ud
            </li>
          </button>
        </ul>}
      </div>
      <a target='_blank' rel='noreferrer' href="https://dantaxi.dk/ofte-stillede-spoergsmaal/">
        <span className={styles.infoIcon}>
          <img src={info} alt="" />
        </span>
      </a>
    </header>
  )
})

export default Header
